/**
 * Starts muted background videos that the device refused to autoplay.
 *
 * The markup is already correct — autoplay, muted, playsinline — and the files
 * are H.264 yuv420p with the moov atom up front. What still blocks playback is
 * the device: iOS Low Power Mode disables autoplay outright and Safari draws
 * its own play button over the first frame; Android Data Saver does the same.
 *
 * A user gesture lifts the restriction, so the first touch, scroll or keypress
 * is used to try again. When autoplay works normally none of this runs.
 *
 * These videos carry no controls, so a paused one is always the browser's doing
 * and never the visitor's — which is why there is no "did they pause it
 * deliberately" bookkeeping here. An earlier version tried to track that and
 * got it wrong: the pause event is asynchronous, so the flag it set outlived
 * the pause it was meant to describe.
 */

const SELECTOR = 'video[autoplay][muted], video[autoplay][playsinline]';

/** play() rejects rather than throwing, and a rejection is the expected signal
 * to wait for a gesture — not an error worth surfacing.
 * @param {HTMLVideoElement} video */
const tryPlay = (video) => video.play().catch(() => {});

const playStalled = () => {
  document.querySelectorAll(SELECTOR).forEach((video) => {
    if (video.paused) tryPlay(video);
  });
};

function init() {
  if (!document.querySelector(SELECTOR)) return;

  playStalled();

  for (const type of ['pointerdown', 'touchstart', 'scroll', 'keydown']) {
    // passive, so listening can never delay a scroll.
    window.addEventListener(type, playStalled, { passive: true });
  }

  // Returning from a background tab is another chance to start cleanly.
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) playStalled();
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
